/**
 * Global website-crawl progress, shown whenever the selected domain has an
 * active crawl run. This is run-driven so it covers both manual "Map website"
 * actions and onboarding-triggered mapping runs.
 */

import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
    KnowledgeOperationProgressProvider,
    useKnowledgeOperationProgress,
} from '@/contexts/KnowledgeOperationProgressContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useWebsiteSyncRunsQuery } from '@/hooks/queries/useWebsiteSyncQueries';
import { ACTIVE_WEBSITE_SYNC_RUN_STATUSES, type WebsiteSyncRun } from '@/services/websiteSync';

interface WebsiteCrawlProgressValue {
    activeRun: WebsiteSyncRun | null;
    isVisible: boolean;
}

export interface RunProgress {
    label: string;
    processed: number | null;
    total: number | null;
    percent: number | null;
}

interface DisplayedRun {
    workspaceId: string;
    run: WebsiteSyncRun;
}

const emptyValue: WebsiteCrawlProgressValue = {
    activeRun: null,
    isVisible: false,
};

const WebsiteCrawlProgressContext = createContext<WebsiteCrawlProgressValue>(emptyValue);

function asRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
}

function numberValue(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringValue(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function progressFromRun(run: WebsiteSyncRun): RunProgress {
    const metadata = asRecord(run.metadata);
    const progress = asRecord(metadata?.progress);
    const processed = numberValue(progress?.processed);
    const total = numberValue(progress?.total);
    const explicitPercent =
        numberValue(progress?.percent) ?? numberValue(progress?.overall_percent);
    const computedPercent =
        processed !== null && total !== null && total > 0
            ? Math.round((processed / total) * 100)
            : null;
    const percent =
        explicitPercent !== null
            ? Math.min(100, Math.max(0, Math.round(explicitPercent)))
            : computedPercent;

    return {
        label:
            stringValue(progress?.label) ??
            (run.status === 'queued'
                ? 'Starting website crawl...'
                : 'Discovering and preparing website pages...'),
        processed,
        total,
        percent,
    };
}

function activeRunFromRuns(runs: WebsiteSyncRun[]): WebsiteSyncRun | null {
    return runs.find((run) => ACTIVE_WEBSITE_SYNC_RUN_STATUSES.has(run.status)) ?? null;
}

function LegacyWebsiteCrawlProgressProvider({ children }: { children: ReactNode }) {
    const { selectedWorkspaceId } = useWorkspace();
    const { isRestoring, progressOwner } = useKnowledgeOperationProgress();
    const useLegacyProgress = !isRestoring && progressOwner('website') === 'legacy';
    const runsQuery = useWebsiteSyncRunsQuery(selectedWorkspaceId);
    const queriedActiveRun = useMemo(
        () => (useLegacyProgress ? activeRunFromRuns(runsQuery.data ?? []) : null),
        [runsQuery.data, useLegacyProgress]
    );
    const [displayedRun, setDisplayedRun] = useState<DisplayedRun | null>(null);
    const activeRun = displayedRun?.workspaceId === selectedWorkspaceId ? displayedRun.run : null;

    useEffect(() => {
        if (!selectedWorkspaceId) {
            setDisplayedRun(null);
            return;
        }
        if (queriedActiveRun) {
            setDisplayedRun({ workspaceId: selectedWorkspaceId, run: queriedActiveRun });
            return;
        }
        if (runsQuery.isLoading || runsQuery.isFetching) {
            setDisplayedRun((current) =>
                current?.workspaceId === selectedWorkspaceId ? current : null
            );
            return;
        }
        setDisplayedRun(null);
    }, [selectedWorkspaceId, queriedActiveRun, runsQuery.isFetching, runsQuery.isLoading]);

    const value = useMemo(
        () => ({
            activeRun,
            isVisible: activeRun !== null,
        }),
        [activeRun]
    );

    // No UI of its own: the Knowledge hub banner and the spinning sidebar mark
    // render this run through useKnowledgeLearning (IX-4673).
    return (
        <WebsiteCrawlProgressContext.Provider value={value}>
            {children}
        </WebsiteCrawlProgressContext.Provider>
    );
}

/**
 * Progress-provider boundary. The durable operation owner must wrap both
 * legacy website progress and RetrainProgressProvider during the cutover.
 */
export function WebsiteCrawlProgressProvider({ children }: { children: ReactNode }) {
    return (
        <KnowledgeOperationProgressProvider>
            <LegacyWebsiteCrawlProgressProvider>{children}</LegacyWebsiteCrawlProgressProvider>
        </KnowledgeOperationProgressProvider>
    );
}

export function useWebsiteCrawlProgressState(): WebsiteCrawlProgressValue {
    return useContext(WebsiteCrawlProgressContext);
}
